import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { FaTimes } from 'react-icons/fa';
import "../styles/TransferPedidoSelector.css";
import "../styles/VentaOtroRevenueSelector.css";
import GroupList from "./GroupList.js";

// ─────────────────────────────────────────────────────────────
// Venta de Otro Revenue — Puntos 2+3 (flujo integrado)
//
// Paso 1: el usuario elige el Revenue de origen (ej. Restaurante).
// Paso 2: se carga products_<idOrigen>.json y se muestra con el mismo
//         componente de navegación por categorías/grupos que ya usa el
//         menú normal del POS (GroupList).
// Paso 3: al elegir un producto, este componente solo avisa hacia arriba
//         (onSelectProduct) cuál producto y de qué Revenue de origen —
//         el cálculo de precio/tax/cargo (revenuePriceOtros) y la
//         resolución del producto local vive en App.js, junto al resto
//         de la lógica de agregar productos al pedido.
// ─────────────────────────────────────────────────────────────
const VentaOtroRevenueSelector = ({
  revenues,
  currentRevenueId,
  fetchOriginProducts,
  onSelectProduct,
  onClose,
  propertyConfig,
}) => {
  const [selectedOriginRevenue, setSelectedOriginRevenue] = useState(null);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [error, setError] = useState(null);

  // ✅ Solo se muestran los Revenue Centers definidos en
  // propertyConfig.revenuePriceOtros (cualquier entrada depto_<id>) — esa
  // lista es la que autoriza qué Revenue puede participar en ventas
  // cruzadas, tanto para vender como para ser origen de un producto.
  const configuredRevenueIds = new Set(
    (propertyConfig?.revenuePriceOtros || [])
      .flatMap((r) => Object.keys(r))
      .filter((k) => k.startsWith("depto_"))
      .map((k) => k.replace("depto_", ""))
  );

  const originRevenues = (revenues || []).filter(
    (r) => r.id !== currentRevenueId && configuredRevenueIds.has(String(r.id))
  );

  const handleSelectOriginRevenue = useCallback(
    async (revenue) => {
      setSelectedOriginRevenue(revenue);
      setProducts([]);
      setError(null);
      setLoadingProducts(true);
      try {
        const data = await fetchOriginProducts(revenue.id);
        setProducts(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(`Error al cargar productos de ${revenue.name}: ${err.message}`);
      } finally {
        setLoadingProducts(false);
      }
    },
    [fetchOriginProducts]
  );

  const handleProductPick = (product) => {
    if (!selectedOriginRevenue || !product) return;
    onSelectProduct(product, selectedOriginRevenue);
    onClose?.();
  };

  return (
    <div className="transfer-pedido-selector venta-otro-revenue-selector">
      <div className="transfer-header">
        <div className="transfer-header-info">
          <h2>🔀 Venta de Otro Revenue</h2>
          <span className="transfer-desde">
            {selectedOriginRevenue ? (
              <>
                Vendiendo producto de: <strong>{selectedOriginRevenue.name}</strong>
              </>
            ) : (
              "Elige el Revenue de origen del producto"
            )}
          </span>
        </div>
        <button className="close-button" onClick={onClose}>
          <FaTimes />
        </button>
      </div>

      {error && <div className="transfer-error-banner">{error}</div>}

      <div className="votr-body">
        <div className="revenue-chips">
          {originRevenues.length === 0 ? (
            <p className="no-results">No hay otros Revenue Centers disponibles</p>
          ) : (
            originRevenues.map((r) => (
              <button
                key={r.id}
                className={`revenue-chip${selectedOriginRevenue?.id === r.id ? " active" : ""}`}
                onClick={() => handleSelectOriginRevenue(r)}
              >
                {r.name}
              </button>
            ))
          )}
        </div>

        {selectedOriginRevenue && (
          <div className="votr-products">
            {loadingProducts ? (
              <div className="loading-message">Cargando productos de {selectedOriginRevenue.name}…</div>
            ) : products.length === 0 ? (
              <div className="empty-message">Sin productos disponibles en {selectedOriginRevenue.name}</div>
            ) : (
              <GroupList
                products={products}
                onSelectProduct={handleProductPick}
                propertyConfig={propertyConfig}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

VentaOtroRevenueSelector.propTypes = {
  revenues: PropTypes.array.isRequired,
  currentRevenueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fetchOriginProducts: PropTypes.func.isRequired,
  onSelectProduct: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  propertyConfig: PropTypes.object,
};

export default VentaOtroRevenueSelector;
